import { createRandomNumber } from './math';
import FingerprintJS from '@fingerprintjs/fingerprintjs'; 
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';

// 激活 isBetween 插件
dayjs.extend(isBetween);
/**
 * 生成唯一 key
 * @param prefix key 前缀
 */
let keyIndex = 0;
export function createKey(prefix: string = '') {
    return `${prefix ? prefix + '' : ''}-${(+new Date()).toString()}-${createRandomNumber()}-${keyIndex++}`;
}

/**
 * 获取当前时间
 */
export function getNow() {
    return new Date().getTime();
}

/**
 * 获取 Unix 时间
 */
export function getUnixTime() {
    return Math.floor(getNow() / 1000);
}

/**
 * 判断是不是在移动端内
 */
export function isMobile() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

/**
 * 同步阻塞
 * @param timeout 延时
 * @returns
 */
export function sleep(timeout = 200) {
    return new Promise((r) => setTimeout(r, timeout));
}

/**
 * 获得指定字符串第 n 个字符串替换的结果
 */
export function replaceChar(str: string, n: number, c: string) {
    const list = str.split('');
    list[n] = c;
    return list.join('');
}

/**
 * 获取文件资源 - 指向 assets/images/
 * @param url
 * @returns
 */
export const getFile = (url: string): string => {
    url = url[0] === '/' ? url.slice(1) : url;
    return new URL(`../${url}`, import.meta.url).href;
};

export const animBasePath = new URL('../assets/image/anim/images', import.meta.url).href;
export const getAnimFile = (url: string): string => {
    url = url[0] === '/' ? url.slice(1) : url;
    return new URL(`../assets/image/anim/images/${url}`, import.meta.url).href;
};
/**
 * 获取图片资源 - 指向 assets/images/
 * @param url
 * @returns
 */
export const getImageFile = (url: string): string => {
    url = url[0] === '/' ? url.slice(1) : url;
    return new URL(`../assets/image/${url}`, import.meta.url).href;
};

export const getModelFile = (url: string): string => {
    url = url[0] === '/' ? url.slice(1) : url;
    return new URL(`../assets/model/${url}`, import.meta.url).href; 
}

export const getVideoFile = (url: string): string => {
    url = url[0] === '/' ? url.slice(1) : url;
    return new URL(`../assets/video/${url}`, import.meta.url).href; 
}

/**
 * 获取 file 文件对象，指向音频
 * @returns 
 */
export async function getFileFromURL(path: string) {
    path = path[0] === '/' ? path.slice(1) : path;
    const url = new URL(`../assets/audio/${path}`, import.meta.url).href
    const response = await fetch(url);
    const blob = await response.blob();
    return new File([blob], url.split('/').pop()!, { type: blob.type });
}

/**
 * 从函数中返回一个随机元素
 * @param array 
 * @returns 
 */
export function getRandomItem<T = unknown>(array: T[]): T {
    if (array.length === 0) {
        throw new Error('Array must not be empty');
    }

    return array[Math.floor(Math.random() * array.length)];
}

/**
 * 获取资源 - 指向 anim/
 * @param url
 * @returns
 */
export const getAniFile = (url: string): string => {
    url = url[0] === '/' ? url.slice(1) : url;
    return new URL(`../assets/anime/${url}.json`, import.meta.url).href;
};

export const flatten = <T>(list: T[][]): T[] => {
    return list.reduce((p, c) => [...p, ...c], []);
}

/**
 * 生成设备指纹
 */
export async function getDeviceFingerPrint() {
    const fpPromise = await FingerprintJS.load();
    const result = await fpPromise.get();
    // Exclude a couple components
    const { fonts, languages, audio, ...components } = result.components;
    return FingerprintJS.hashComponents(components);
}

/**
 * 确认文字是否符合识别结果
 */
export function checkText(text: string) {
    if (text.includes('至') && text.includes('熠')) return true;
    const list = getLongSubstrings('ESSENSISNEO', 4);
    for (let i = 0; i < list.length; i++) {
        if (text.includes(list[i])) return true;
    }
    return false;
}

function getLongSubstrings(str: string, minLength: number) {
    const result: string[] = [];
    const length = str.length;

    // 生成所有可能的子串
    for (let start = 0; start < length; start++) {
        for (let end = start + 1; end <= length; end++) {
            const substring = str.slice(start, end);
            // 检查子串长度
            if (substring.length > minLength) {
                result.push(substring);
            }
        }
    }

    return result;
}

/**
 * 裁剪图片
 */
export async function cropImage(originBase64: string): Promise<string> {
    return await new Promise<string>((r, rj) => {
        const image = new Image();
        image.src = 'data:image/jpeg;base64,' + originBase64;
        image.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                rj('无法获取 canvas 上下文');
                return;
            }

            // 设置 canvas 尺寸
            const width = image.width;
            const height = image.height;
            canvas.width = width;
            canvas.height = height;

            // 将图像绘制到 canvas
            ctx.drawImage(image, 0, 0);

            // 计算中间部分的裁剪区域
            const left = Math.floor(width / 3);
            const top = Math.floor(height / 3);
            const cropWidth = Math.floor(width / 3);
            const cropHeight = Math.floor(height / 3);

            // 从 canvas 中提取中间部分
            const imageData = ctx.getImageData(left, top, cropWidth, cropHeight);
            canvas.width = cropWidth;
            canvas.height = cropHeight;
            ctx.putImageData(imageData, 0, 0);

            // 将 canvas 转换为 Base64
            const croppedBase64 = canvas.toDataURL();
            r(croppedBase64);
        }
    })
}

/**
 * 节流函数
 * @returns 
 */
export function throttle<T extends (...args: any[]) => void>(fn: T, wait: number) {
    let lastTime = 0;
    return function(this: any, ...args: Parameters<T>) {
        const now = Date.now();
        if (now - lastTime >= wait) {
            lastTime = now;
            return fn.apply(this, args);
        }
    };
}

/** 用于判断当前时间是否在隐藏期间内 */
export function isInTimeRange() {
  const currentTime = dayjs();
  const startTime = dayjs('2025-09-20 00:00:00');
  const endTime = dayjs('2025-09-23 23:59:59');

  return currentTime.isBetween(startTime, endTime, null, '[]');
}